import React, { useState, useEffect } from 'react';
import { fetchPurchases, savePurchase, fetchProducts, fetchCustomers } from '../../services/api';
import './Purchase.css';

const today = '01-04-2026';

export default function Purchase({ user }) {

  const [records, setRecords] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const companyId = user?.Companyid || user?.company_id || "1";

  const [form, setForm] = useState({
    bill_no: '',
    date: today,
    supplier: '',
    product: 'Petrol',
    qty: '',
    rate: '',
    payment_mode: 'Credit'
  });

  useEffect(() => {
    loadData();
  }, [companyId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPurchases({
        Companyid: companyId,
        Name: "",
        Product: "",
        FromDate: "01-03-2026",
        ToDate: "01-04-2026"
      });
      if (res.success) {
        const raw = res.Response;
        const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
        setRecords(list);
      }

      const prodRes = await fetchProducts(companyId);
      if (prodRes.success) setProducts(prodRes.Response || []);

      const suppRes = await fetchCustomers(companyId);
      if (suppRes.success) {
        const allSupp = suppRes.Response || [];
        setSuppliers(allSupp.filter(s => 
          s.Type === "Party" || s.Type === "Suppliers" || s.Type === "Supplier"
        ));
      }

    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {

    if (!form.qty || !form.rate || !form.supplier) {
      alert("Please fill all required fields");
      return;
    }

    const qty = parseFloat(form.qty);
    const rate = parseFloat(form.rate);
    const amount = qty * rate;

    const apiData = {
      Companyid: companyId,
      Name: form.supplier,
      Product: form.product,
      Qty: qty.toString(),
      Rate: rate.toString(),
      Amount: amount.toFixed(2),
      Tax: "0",
      TaxAmount: "0",
      Total: amount.toFixed(2),
      Date1: form.date,
      PaymentMode: form.payment_mode,
      BillNo: form.bill_no,
      IDS: "0"
    };

    setLoading(true);
    try {
      const res = await savePurchase(apiData);
      if (res.success || res.Response === "UPDATED" || res.Response === "INSERTED") {
        alert(res.message || "Purchase saved successfully!");
        loadData();
        setShowForm(false);
        setForm({
          bill_no: '',
          date: today,
          supplier: '',
          product: 'Petrol',
          qty: '',
          rate: '',
          payment_mode: 'Credit'
        });
      } else {
        alert(res.message || "Failed to save record");
      }
    } catch (err) {
      console.error('Save error:', err);
      alert("Error connecting to server");
    } finally {
      setLoading(false);
    }

  };

  const total = records.reduce((s, r) => s + (parseFloat(r.Total || r.Amount || r.amount || 0)), 0);

  if (loading && records.length === 0) return <div style={{padding: 20}}>Loading...</div>;

  return (

    <div className="purchase-page">

      <div className="page-header">

        <h2 className="page-title">Purchase / Orders</h2>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? '✕ Close Form' : '＋ New Order'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={loadData}>
             <i className="bi bi-arrow-clockwise"></i>
          </button>
        </div>

      </div>

      {showForm && (

        <div className="section-card">

          <div className="section-header">New Purchase Order</div>

          <div className="form-row">

            <div className="form-group">
              <label>Bill / PO No.</label>
              <input
                className="form-control"
                value={form.bill_no}
                onChange={e => setForm({ ...form, bill_no: e.target.value })}
                placeholder="PO-XXXX"
              />
            </div>

            <div className="form-group">
              <label>Date</label>
              <input
                className="form-control"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Supplier</label>
              <select
                className="form-control"
                value={form.supplier}
                onChange={e => setForm({ ...form, supplier: e.target.value })}
              >
                <option value="">Select Supplier</option>
                {suppliers.map(s => (
                  <option key={s.Customerid || s.id} value={s.Name || s.name}>{s.Name || s.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Product</label>
              <select
                className="form-control"
                value={form.product}
                onChange={e => setForm({ ...form, product: e.target.value })}
              >
                {products.map(p => (
                   <option key={p.Productid || p.id} value={p.Name || p.name}>{p.Name || p.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Quantity</label>
              <input
                type="number"
                className="form-control"
                value={form.qty}
                onChange={e => setForm({ ...form, qty: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Rate (₹)</label>
              <input
                type="number"
                className="form-control"
                value={form.rate}
                onChange={e => setForm({ ...form, rate: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Payment Mode</label>
              <select
                className="form-control"
                value={form.payment_mode}
                onChange={e => setForm({ ...form, payment_mode: e.target.value })}
              >
                <option>Credit</option>
                <option>Cash</option>
                <option>UPI</option>
              </select>
            </div>

            {form.qty && form.rate && (
              <div className="form-group">
                <label>Amount</label>
                <div
                  style={{
                    padding: '8px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    fontWeight: 700,
                    color: 'var(--secondary)',
                    backgroundColor: '#f0fff4'
                  }}
                >
                  ₹{(parseFloat(form.qty) * parseFloat(form.rate)).toLocaleString()}
                </div>
              </div>
            )}

          </div>

          <div className="action-bar" style={{ padding: '0 16px 14px' }}>

            <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
                 <i className="bi bi-save me-1"></i> {loading ? 'Saving...' : 'Save Record'}
            </button>

            <button
              className="btn btn-secondary"
              onClick={() => setShowForm(false)}
            >
              ✕ Cancel
            </button>

          </div>

        </div>

      )}

      <div className="section-card">

        <div className="section-header">
          Purchase Records — {records.length} records | Total: ₹{total.toLocaleString()}
        </div>

        <div className="table-wrapper">

          <table className="data-table">

            <thead>

              <tr>
               
                <th style={{ textAlign: 'center' }}>Bill No.</th>
                <th style={{ textAlign: 'center' }}>Date</th>
                <th style={{ textAlign: 'left' }}>Supplier</th>
                <th style={{ textAlign: 'center' }}>Product</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>Rate (₹)</th>
                <th style={{ textAlign: 'right' }}>Amount (₹)</th>
                <th style={{ textAlign: 'center' }}>Status</th>
              </tr>

            </thead>

            <tbody>
              {records.length === 0 ? (
                 <tr><td colSpan="8" style={{textAlign:'center', padding:20}}>No records found</td></tr>
              ) : (
                records.map((r, i) => (

                  <tr key={i}>

                    <td
                      style={{
                        textAlign: 'center',
                        fontFamily: 'monospace',
                        color: 'var(--primary)',
                        fontWeight: 700
                      }}
                    >
                      {r.BillNo || r.billNo || '—'}
                    </td>

                    <td style={{ textAlign: 'center' }}>
                      {r.Date1 || r.date}
                    </td>

                    <td style={{ fontWeight: 600 }}>
                      {r.Customer || r.supplier || r.Name || '—'}
                    </td>

                    <td style={{ textAlign: 'center' }}>
                      <span className="badge badge-info">
                        {r.Product || r.product}
                      </span>
                    </td>

                    <td style={{ textAlign: 'right' }}>
                      {parseFloat(r.Qty || r.qty || 0).toLocaleString()}
                    </td>

                    <td style={{ textAlign: 'right' }}>
                      ₹{parseFloat(r.Rate || r.rate || 0).toFixed(2)}
                    </td>

                    <td
                      style={{
                        textAlign: 'right',
                        fontWeight: 700
                      }}
                    >
                      ₹{parseFloat(r.Total || r.Amount || r.amount || 0).toLocaleString()}
                    </td>

                    <td style={{ textAlign: 'center' }}>
                       <span className={`badge ${(r.PaymentMode || '').toUpperCase() === 'CASH' ? 'badge-success' : 'badge-warning'}`}>
                        {r.PaymentMode || 'Credit'}
                      </span>
                    </td>

                  </tr>

                ))
              )}

            </tbody>

          </table>

        </div>

      </div>

    </div>

  );

}